import { observable, makeObservable } from 'mobx';
import isNil from 'lodash/isNil';

import type {
    PriceData,
    PriceCreateForm,
    PriceUpdateForm,
    PriceStatusUpdateForm,
} from 'chaika-frontend-types/frontend-cms';

import { Currency, PriceStatus } from 'chaika-enums';

import { Money } from '@store/models/Money';
import { userService } from '@user/services/userService';
import { store } from '@store';

export interface PriceModelData extends Omit<PriceData, 'value'> {
    value?: Money;
    baseValue?: string;
    author?: string;
    contractorName?: string;
    userName?: string;
    legalEntityNames?: string;
}

export interface PriceCreationParams extends Omit<PriceCreateForm, 'value'> {
    value: Money;
}

export interface PriceUpdateParams extends Omit<PriceUpdateForm, 'value'> {
    value?: Money;
}

export class Price implements PriceModelData {
    public static fromData({ value, ...data }: PriceData): Price {
        return new Price({ ...data, value: Money.fromKopecks(value, data.currency) });
    }

    public static fromForm(data: PriceCreationParams | PriceUpdateParams): Price {
        return new Price(data as PriceModelData);
    }

    @observable public id: string;
    @observable public serviceId: string;
    @observable public authorId: string;
    @observable public value?: Money;
    @observable public status: PriceStatus;
    @observable public currency: Currency;
    @observable public createdAt: string;
    @observable public scheduledOn: string;
    @observable public isComposite: boolean;
    @observable public customReferralRule?: boolean;
    @observable public includedServices: string[];
    @observable public legalEntityIds: string[];
    @observable public contractorId?: string | null;
    @observable public userId?: string | null;
    @observable public payerId?: string | null;
    @observable public archivedAt?: string | null;

    private constructor(data: PriceModelData = {} as PriceModelData) {
        makeObservable(this);

        this.id = data.id;
        this.serviceId = data.serviceId;
        this.authorId = data.authorId;
        this.value = data.value;
        this.status = data.status;
        this.createdAt = data.createdAt;
        this.scheduledOn = data.scheduledOn;
        this.legalEntityIds = data.legalEntityIds;
        this.contractorId = data.contractorId;
        this.currency = data.currency;
        this.includedServices = data.includedServices ?? [];
        this.userId = data.userId;
        this.isComposite = data.isComposite ?? false;
        this.customReferralRule = data.customReferralRule;
        this.archivedAt = data.archivedAt;
        this.payerId = data.payerId;
    }

    public isBasePrice(): boolean {
        return isNil(this.userId);
    }

    public isActive(): boolean {
        return this.status === PriceStatus.Active;
    }

    public isPlanned(): boolean {
        return this.status === PriceStatus.Planned;
    }

    public update(data: PriceUpdateParams | PriceStatusUpdateForm): void {
        for (const key in data) {
            // @ts-expect-error
            this[key] = data[key];
        }
    }

    public serialize(): PriceData {
        return { ...this, value: this.value?.kopecks ?? 0 };
    }

    public makeEditTableData(): PriceModelData {
        return {
            ...this.serialize(),
            userName: this.userId ? userService.getUser(this.authorId)?.fullName : '',
            contractorName: this.contractorId ? store.organizationList.get(this.contractorId)?.name : '',
            legalEntityNames: this.legalEntityIds.map(id => store.legalEntityList.get(id)?.name).join(', '),
            value: this.value,
            author: userService.getUser(this.authorId)?.shortFullName ?? '',
            status: this.status,
            archivedAt: this.archivedAt || '',
        };
    }
}
